/**
 * LIVEL — Kurjeris (nurodytu adresu) pasirinkimas.
 *
 * Skirtingai nuo Omniva/LP EXPRESS, Kurjeris NEREIKALAUJA paštomato
 * paieškos — klientas adresą įveda pats checkout metu. Šis failas tik:
 *  1) išsaugo livel_delivery_carrier = 'Kurjeris' cart attribute ir išvalo
 *     visus livel_delivery_locker_* — TIE PATYS raktai, kuriuos jau naudoja
 *     cart-omniva.js/cart-lpexpress.js, jokios naujos schema;
 *  2) pašalina bet kokį anksčiau (per Omniva/LP EXPRESS) pridėtą pasirinktą
 *     pristatymo adresą iš cart.delivery.addresses (Storefront API
 *     cartDeliveryAddressesRemove) — kad checkout NErodytų seno paštomato
 *     adreso, kai klientas nori patys įvesti savo adresą, ir vietoj jo
 *     prideda tuščią adresą su address2 = "Kurjeris" žymeniu (worker /rates);
 *  3) per TĄ PATĮ modelį kaip cart-omniva.js/cart-lpexpress.js
 *     setSelectedDeliveryOption() (dinamiškai randa handle pagal
 *     deliveryOptions title, jokių hardcoded ID/handle), priverstinai
 *     pažymi checkout shipping method radio "Kurjeris (nurodytu adresu)";
 *  4) transliuoja TĄ PATĮ 'livel:delivery-selection-changed' CustomEvent
 *     formatą, kad Omniva/LP EXPRESS widget'ai (jau esanti logika,
 *     nepakeista) patys persijungtų į idle būseną.
 *
 * NELIEČIA: shipping tarifų, Paysera, email šablonų,
 * /pricing ar laisvo pristatymo ribos skaičiavimo logikos.
 */

const API_VERSION = '2025-10';

async function storefrontQuery(query, variables) {
  const res = await fetch(`/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables })
  });
  return res.json();
}

// Tas pats broadcast formatas kaip cart-omniva.js/cart-lpexpress.js — žr. tų
// failų broadcastDeliverySelection() komentarą. Kurjeriui name/address/city/
// zip visada tušti, nes locker laukai išvalyti.
function broadcastDeliverySelection(cart) {
  const attrs = (cart && cart.attributes) || {};
  document.dispatchEvent(new CustomEvent('livel:delivery-selection-changed', {
    detail: {
      carrier: attrs.livel_delivery_carrier || '',
      name: attrs.livel_delivery_locker_name || '',
      address: attrs.livel_delivery_locker_address || '',
      city: attrs.livel_delivery_locker_city || '',
      zip: attrs.livel_delivery_locker_zip || '',
      cart: cart || null
    }
  }));
}

// Identiška cart-omniva.js/cart-lpexpress.js setSelectedDeliveryOption() —
// žr. ten pilną komentarą. HANDLE niekada nehardcodinamas, visada randamas
// dinamiškai iš dabartinio cart.deliveryGroups sąrašo.
async function setSelectedDeliveryOption(cartGid, titleKeywords) {
  try {
    const groupsResult = await storefrontQuery(
      `query LivelGetDeliveryGroups($cartId: ID!) {
        cart(id: $cartId) {
          deliveryGroups(first: 10) {
            edges { node { id deliveryOptions { handle title } } }
          }
        }
      }`,
      { cartId: cartGid }
    );
    const edges =
      (groupsResult.data &&
        groupsResult.data.cart &&
        groupsResult.data.cart.deliveryGroups &&
        groupsResult.data.cart.deliveryGroups.edges) ||
      [];

    let deliveryGroupId = null;
    let deliveryOptionHandle = null;
    for (const edge of edges) {
      const group = edge.node;
      const match = group.deliveryOptions.find((option) => {
        const title = (option.title || '').toLowerCase();
        return titleKeywords.some((keyword) => title.includes(keyword));
      });
      if (match) {
        deliveryGroupId = group.id;
        deliveryOptionHandle = match.handle;
        break;
      }
    }

    if (!deliveryGroupId || !deliveryOptionHandle) return;

    const updateResult = await storefrontQuery(
      `mutation LivelSetDeliveryOption($cartId: ID!, $selectedDeliveryOptions: [CartSelectedDeliveryOptionInput!]!) {
        cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $selectedDeliveryOptions) {
          userErrors { field message code }
          warnings { message code }
        }
      }`,
      {
        cartId: cartGid,
        selectedDeliveryOptions: [{ deliveryGroupId, deliveryOptionHandle }]
      }
    );
    const payload = updateResult.data && updateResult.data.cartSelectedDeliveryOptionsUpdate;
    if (payload && ((payload.userErrors && payload.userErrors.length) || (payload.warnings && payload.warnings.length))) {
      // eslint-disable-next-line no-console
      console.warn('LIVEL cartSelectedDeliveryOptionsUpdate (Kurjeris):', payload.userErrors, payload.warnings);
    }
  } catch (e) {
    // Convenience only — cart attributes jau užtikrina merchant-visible
    // pasirinkimą; jei ši mutacija nepavyksta, klientas tiesiog turi pats
    // rankiniu būdu pasirinkti teisingą radio checkout'e.
  }
}

// Pašalina bet kokį esamą pasirinktą pristatymo adresą (pvz. anksčiau
// pridėtą Omniva/LP EXPRESS paštomato adresą per cartDeliveryAddressesAdd),
// kad checkout NErodytų seno paštomato adreso vietoje tuščio lauko, kai
// klientas renkasi Kurjerį ir nori patys įvesti savo adresą.
async function removeExistingDeliveryAddresses(cartGid) {
  try {
    const result = await storefrontQuery(
      `query LivelGetDeliveryAddresses($cartId: ID!) {
        cart(id: $cartId) {
          delivery { addresses { id } }
        }
      }`,
      { cartId: cartGid }
    );
    const addresses =
      (result.data &&
        result.data.cart &&
        result.data.cart.delivery &&
        result.data.cart.delivery.addresses) ||
      [];
    const addressIds = addresses.map((a) => a.id).filter(Boolean);
    if (!addressIds.length) return;

    const removeResult = await storefrontQuery(
      `mutation LivelRemoveAddresses($cartId: ID!, $addressIds: [ID!]!) {
        cartDeliveryAddressesRemove(cartId: $cartId, addressIds: $addressIds) {
          userErrors { field message }
        }
      }`,
      { cartId: cartGid, addressIds }
    );
    const errors =
      removeResult.data &&
      removeResult.data.cartDeliveryAddressesRemove &&
      removeResult.data.cartDeliveryAddressesRemove.userErrors;
    if (errors && errors.length) {
      // eslint-disable-next-line no-console
      console.warn('LIVEL cartDeliveryAddressesRemove:', errors);
    }
  } catch (e) {
    // Convenience only — jei pašalinti nepavyksta, klientas checkout'e vis
    // tiek gali pats rankiniu būdu pakeisti/perrašyti adresą prieš apmokėjimą.
  }
}

// Checkout'o "address2" laukas paslėptas, todėl jame paliekamas žymuo
// "Kurjeris" — CarrierService (worker /rates) pagal jį rodo TIK kurjerio
// būdą. Be žymens tuščias address2 atrodytų kaip Apple Pay / Google Pay
// adresas, kuriam worker rodo visus būdus. Klientas pats įveda adresą.
async function addCourierMarkerAddress(cartGid) {
  try {
    const result = await storefrontQuery(
      `mutation LivelSetCourierMarker($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
        cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {
          userErrors { field message }
        }
      }`,
      {
        cartId: cartGid,
        addresses: [
          {
            address: { deliveryAddress: { address2: 'Kurjeris', countryCode: 'LT' } },
            selected: true,
            oneTimeUse: true
          }
        ]
      }
    );
    const errors =
      result.data &&
      result.data.cartDeliveryAddressesAdd &&
      result.data.cartDeliveryAddressesAdd.userErrors;
    if (errors && errors.length) {
      // eslint-disable-next-line no-console
      console.warn('LIVEL cartDeliveryAddressesAdd (Kurjeris):', errors);
    }
  } catch (e) {
    // Jei nepavyksta, checkout'as tiesiog parodo visus būdus, kaip anksčiau.
  }
}

async function selectCourier(root) {
  const updateRes = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        livel_delivery_carrier: 'Kurjeris',
        livel_delivery_locker_name: '',
        livel_delivery_locker_address: '',
        livel_delivery_locker_city: '',
        livel_delivery_locker_zip: ''
      }
    })
  });
  const cart = await updateRes.json();
  const cartGid = `gid://shopify/Cart/${cart.token}`;

  await removeExistingDeliveryAddresses(cartGid);
  await addCourierMarkerAddress(cartGid);
  await setSelectedDeliveryOption(cartGid, ['kurjeris']);

  broadcastDeliverySelection(cart);
}

function wireCourierRadio(root) {
  if (root.dataset.cartCourierInitialized) return;
  root.dataset.cartCourierInitialized = 'true';

  const radio = root.querySelector('[data-cart-delivery-radio][value="Kurjeris"]');
  if (!radio) return;

  radio.addEventListener('change', () => {
    if (!radio.checked) return;
    selectCourier(root);
  });
}

function initAll() {
  document.querySelectorAll('[data-cart-delivery]').forEach(wireCourierRadio);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// Re-init after Shopify Section Rendering API swaps (cart drawer / cart page
// re-renders after quantity changes etc.) — tas pats principas kaip
// cart-delivery.js/cart-omniva.js/cart-lpexpress.js.
document.addEventListener('shopify:section:load', initAll);

// Re-init whenever a NEW, not-yet-wired [data-cart-delivery] element appears
// anywhere in the DOM (AJAX/innerHTML script-execution problema — žr. pilną
// paaiškinimą cart-omniva.js faile).
const cartCourierObserver = new MutationObserver(() => {
  const unwired = document.querySelector('[data-cart-delivery]:not([data-cart-courier-initialized])');
  if (unwired) initAll();
});
cartCourierObserver.observe(document.body, { childList: true, subtree: true });
